import type { Directive } from 'vue'
import { isShowingNumber, writeNumber } from '@/globals/typescript/utils/numberInputValue'

/**
 * `v-number-value="storedNumber"` — keeps a native number input showing a stored number, without
 * overwriting what the user is typing. Use it instead of `:value` on an `<input type="number">`
 * whose `@input` handler parses the text into that number.
 *
 * The field is written on mount, and again only when it shows a different number than the stored
 * one (a value that changed from outside: a reset, a special value, a live sync). While typing,
 * "0,0" is the number 0 and is left as typed, so the next keystroke can make it "0,05".
 *
 * `.emptyIsZero` for a field whose stored value cannot be empty and reads 0 when the text is.
 */
export const vNumberValue: Directive<HTMLInputElement, number | null | undefined> = {
  mounted(el, binding) {
    writeNumber(el, binding.value ?? null)
  },
  updated(el, binding) {
    const value = binding.value ?? null
    if (isShowingNumber(el, value, { emptyIsZero: !!binding.modifiers.emptyIsZero })) return
    writeNumber(el, value)
  },
}
